import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface UploadResponse {
  success: boolean;
  message?: string;
  links?: any[];
  error?: string;
}

interface LinkData {
  url: string;
  capacity: number;
}

interface ValidationResult {
  valid: boolean;
  filteredLinks: LinkData[];
  errorMessage?: string;
}

interface UploadProgressState {
  total: number;
  uploaded: number;
  failed: number;
  inProgress: boolean;
}

export const useFollowLinksUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgressState>({
    total: 0,
    uploaded: 0,
    failed: 0,
    inProgress: false
  });
  const { toast } = useToast();

  /**
   * Validates and filters the links before upload
   * @param links Array of link objects or raw URL strings from user input
   * @returns ValidationResult with filtered array and validation status
   */
  const validateLinks = (links: (string | LinkData)[]): ValidationResult => {
    if (!links || links.length === 0) {
      return { 
        valid: false, 
        filteredLinks: [],
        errorMessage: "No links provided" 
      };
    }
    
    // Process and filter links
    try {
      const filteredLinks: LinkData[] = links
        .map(link => {
          // If it's already a LinkData object
          if (typeof link === 'object' && link !== null) {
            if (!link.url || typeof link.url !== 'string' || !link.url.trim()) {
              throw new Error("Invalid URL format");
            }
            
            // Ensure capacity is a valid number
            const capacity = typeof link.capacity === 'number' ? link.capacity : parseInt(String(link.capacity), 10);
            if (isNaN(capacity) || capacity <= 0) {
              throw new Error(`Invalid capacity for ${link.url}: must be a positive number`);
            }
            
            return {
              url: link.url.trim(),
              capacity
            };
          }
          // If it's a string (backward compatibility)
          else if (typeof link === 'string') {
            return {
              url: link.trim(),
              capacity: 1000 // Default capacity
            };
          }
          
          throw new Error("Invalid link format");
        })
        .filter(link => link.url.length > 0);
      
      if (filteredLinks.length === 0) {
        return { 
          valid: false, 
          filteredLinks: [],
          errorMessage: "No valid links to upload after filtering" 
        };
      }
      
      return { valid: true, filteredLinks };
    } catch (error) {
      return {
        valid: false,
        filteredLinks: [],
        errorMessage: error instanceof Error ? error.message : "Invalid link data format"
      };
    }
  };

  /**
   * Handles error responses from the edge function
   * @param error The error object returned from the function call
   * @returns Formatted error message
   */
  const formatErrorMessage = (error: any): string => {
    console.error("Error uploading follow links:", error);
    
    if (error.status === 401 || error.statusCode === 401) {
      return "Authentication error: You need to log in again to upload links";
    } 
    
    if (error.status === 403 || error.statusCode === 403) {
      return "Permission denied: You don't have access to upload links for this account";
    } 
    
    if (error.name === "TypeError" && error.message.includes("networkerror")) {
      return "Network error: Please check your connection and try again";
    }
    
    if (error.name === "FunctionsFetchError" || (error.message && error.message.includes("Edge Function"))) {
      return "Server error: The request was too large or timed out. Your links are being uploaded in batches.";
    }
    
    if (error.message) {
      return error.message;
    } 
    
    if (error.data && typeof error.data === 'object') {
      return error.data.error || error.data.message || "Unknown error occurred";
    }
    
    return "Failed to upload follow links";
  };

  /**
   * Checks the authentication status before proceeding with upload
   * @returns Promise<boolean> indicating if user is authenticated
   */
  const checkAuthStatus = async (): Promise<boolean> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to upload follow links",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  /**
   * Uploads a batch of links to the specified account
   * @param accountId The account ID to upload links for
   * @param linksBatch The batch of link data objects to upload
   * @param batchNumber The current batch number (for logging)
   * @returns Success status and any error message
   */
  const uploadLinksBatch = async (
    accountId: string,
    linksBatch: LinkData[],
    batchNumber: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log(`Uploading batch ${batchNumber} (${linksBatch.length} links)`);
      
      const { data, error } = await supabase.functions.invoke<UploadResponse>("bulk-upload-follow-links", {
        body: {
          accountId,
          links: linksBatch
        }
      });

      if (error) {
        console.error(`Error in batch ${batchNumber}:`, error);
        return { success: false, error: formatErrorMessage(error) };
      }

      if (!data) {
        console.error(`Batch ${batchNumber} received empty response`);
        return { success: false, error: "Received empty response from server" };
      }

      console.log(`Batch ${batchNumber} uploaded successfully:`, data.message);
      return { success: true };
    } catch (error: any) {
      console.error(`Exception in batch ${batchNumber}:`, error);
      return { success: false, error: formatErrorMessage(error) };
    }
  };

  /**
   * Splits an array into chunks of the specified size
   * @param array The array to split
   * @param chunkSize The maximum size of each chunk
   * @returns Array of chunks
   */
  const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  /**
   * Uploads links in batches with automatic retry for failed batches
   * @param accountId The account ID to upload links for
   * @param links Array of link data objects to upload
   * @param batchSize The maximum number of links per batch
   * @param maxRetries Maximum number of retry attempts for failed batches
   * @returns Promise resolving to overall success status
   */
  const uploadLinksBatched = async (
    accountId: string,
    links: LinkData[],
    batchSize: number = 100,
    maxRetries: number = 2
  ): Promise<boolean> => {
    // Split links into batches
    const batches = chunkArray(links, batchSize);
    console.log(`Split ${links.length} links into ${batches.length} batches of max ${batchSize} links each`);
    
    // Initialize progress
    setProgress({
      total: links.length,
      uploaded: 0,
      failed: 0,
      inProgress: true
    });
    
    let successfulLinks = 0;
    let failedLinks = 0;
    
    // Process batches sequentially
    for (let i = 0; i < batches.length; i++) {
      const currentBatch = batches[i];
      let batchSuccess = false;
      let retryCount = 0;
      let lastError = "";
      
      // Try uploading the batch with retries
      while (!batchSuccess && retryCount <= maxRetries) {
        if (retryCount > 0) {
          console.log(`Retrying batch ${i + 1} (attempt ${retryCount}/${maxRetries})`);
          
          // Show retry toast
          toast({
            title: `Retrying batch ${i + 1}`,
            description: `Attempt ${retryCount} of ${maxRetries}`,
            variant: "default",
          });
          
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
        
        const { success, error } = await uploadLinksBatch(accountId, currentBatch, i + 1);
        
        if (success) {
          batchSuccess = true;
        } else {
          lastError = error || "Unknown error";
          retryCount++;
        }
      }
      
      // Update progress based on batch result
      if (batchSuccess) {
        successfulLinks += currentBatch.length;
        setProgress(prev => ({
          ...prev,
          uploaded: successfulLinks,
          failed: failedLinks
        }));
      } else {
        failedLinks += currentBatch.length;
        setProgress(prev => ({
          ...prev,
          uploaded: successfulLinks,
          failed: failedLinks
        }));
        
        // Show toast for batch failure but continue with next batch
        toast({
          title: `Batch ${i + 1} failed after ${maxRetries} attempts`,
          description: lastError,
          variant: "destructive",
        });
      }
    }
    
    // Update final progress state
    setProgress(prev => ({
      ...prev,
      inProgress: false
    }));
    
    // Return overall success status
    return successfulLinks > 0;
  };

  /**
   * Uploads a list of follow links with capacity values to a specific account
   * @param accountId The ID of the account to upload links to
   * @param links Array of link data objects or URL strings to upload
   * @returns Promise<boolean> indicating success or failure
   */
  const uploadFollowLinks = async (accountId: string, links: (string | LinkData)[]): Promise<boolean> => {
    // Check authentication first
    const isAuthenticated = await checkAuthStatus();
    if (!isAuthenticated) return false;
    
    // Validate links
    const validation = validateLinks(links);
    
    if (!validation.valid) {
      toast({
        title: "Error",
        description: validation.errorMessage || "No valid links to upload",
        variant: "destructive",
      });
      return false;
    }
    
    setIsUploading(true);
    
    try {
      console.log(`Starting upload of ${validation.filteredLinks.length} follow links to account ${accountId}`);
      
      // Determine optimal batch size based on total number of links
      let batchSize: number;
      if (validation.filteredLinks.length > 500) {
        batchSize = 50;
      } else if (validation.filteredLinks.length > 250) {
        batchSize = 75;
      } else if (validation.filteredLinks.length > 100) {
        batchSize = 100;
      } else {
        batchSize = 125;
      }
      
      // Upload links in batches
      const success = await uploadLinksBatched(accountId, validation.filteredLinks, batchSize);
      
      // Show final toast based on overall result
      if (success) {
        const uploadedCount = progress.uploaded;
        const failedCount = progress.failed;
        
        if (failedCount > 0) {
          toast({
            title: "Partial Upload Success",
            description: `Successfully uploaded ${uploadedCount} links, but ${failedCount} links failed.`,
            variant: "default",
          });
        } else {
          toast({
            title: "Success",
            description: `Successfully uploaded all ${uploadedCount} follow links`,
            variant: "default",
          });
        }
      } else {
        toast({
          title: "Upload Failed",
          description: "Failed to upload follow links. Please try again with fewer links.",
          variant: "destructive",
        });
      }
      
      return success;
    } catch (error: any) {
      const errorMessage = formatErrorMessage(error);
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadFollowLinks, isUploading, progress };
};
